import { useEffect } from 'react';

import { standardSchemaResolver } from '@hookform/resolvers/standard-schema';
import dayjs from 'dayjs';
import { useRouter } from 'next/navigation';
import type { FieldErrors, UseFormRegister, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { boolean, object, string } from 'zod/v4';

import { appConfig } from '@/config/appConfig';
import useRegexPatterns from '@/features/regexPatterns/hooks/useRegexPatterns';
import { getValidationErrorMessage } from '@/helpers';
import useI18n from '@/hooks/useI18n';
import usePost from '@/hooks/usePost';
import type { ErrorResponse } from '@/types/ErrorResponse';

import { authRoutes } from '../routes/authRoutes';
import type { LoginRequestBody } from '../types/LoginRequestBody';
import type { LoginResponse } from '../types/LoginResponse';

import useAuthStore from './useAuthStore';

export type FormValues = {
    identifier: string;
    password: string;
    rememberMe: boolean;
};

type UseLoginReturn = {
    isValid: boolean;
    isLoading: boolean;
    response: LoginResponse | null;
    error: ErrorResponse | null;
    validationErrors: FieldErrors<FormValues>;
    submit: () => void;
    register: UseFormRegister<FormValues>;
    setValue: UseFormSetValue<FormValues>;
    watch: UseFormWatch<FormValues>;
};

export default function useLogin(): UseLoginReturn {
    // ----------------------------------------------------------------------------------------------------
    // MARK: States & Constants
    // ----------------------------------------------------------------------------------------------------
    const { t } = useI18n();
    const router = useRouter();
    const { regexPatterns } = useRegexPatterns();

    const updateUser = useAuthStore((state) => state.updateUser);
    const updateToken = useAuthStore((state) => state.updateToken);
    const { submit: submitHandler, response, isLoading, error, validationErrors } = usePost<LoginResponse, ErrorResponse>();

    const schema = object({
        identifier: string()
            .nonempty({ message: t('Auth.Validations.Identifier_Required') })
            .min(4, { message: t('Auth.Validations.Identifier_Minimum_Length') })
            .max(255, { message: t('Auth.Validations.Identifier_Maximum_Length') }),
        password: string()
            .nonempty({ message: t('Auth.Validations.Password_Required') })
            .min(8, { message: t('Auth.Validations.Password_Minimum_Length') })
            .max(64, { message: t('Auth.Validations.Password_Maximum_Length') })
            .regex(regexPatterns?.users.password ?? /^/, {
                message: t('Auth.Validations.Password_Regex_Error'),
            }),
        rememberMe: boolean({ message: t('Auth.Validations.Remember_Me_Required') }),
    });

    const {
        register,
        formState: { errors, isValid },
        setError,
        watch,
        setValue,
    } = useForm({
        resolver: standardSchemaResolver(schema),
        mode: appConfig.formValidation.mode,
        defaultValues: {
            identifier: '',
            password: '',
            rememberMe: true,
        },
    });

    const { identifier, password, rememberMe } = watch();

    // ----------------------------------------------------------------------------------------------------
    // MARK: Functions
    // ----------------------------------------------------------------------------------------------------
    function submit(): void {
        const requestBody: LoginRequestBody = {
            identifier: identifier.toLowerCase(),
            password,
            rememberMe,
        };

        submitHandler(authRoutes.api.login(), requestBody, {
            onSuccess: (res) => {
                void (async (): Promise<void> => {
                    await updateUser(res.user, { expires: rememberMe ? dayjs().add(7, 'days').toDate() : dayjs().endOf('day').toDate() });
                    await updateToken(res.token, { expires: rememberMe ? dayjs().add(7, 'days').toDate() : dayjs().endOf('day').toDate() });

                    // Now cookies are guaranteed to be set, safe to refresh
                    router.refresh(); // Server refresh
                })();
            },
        });
    }

    // ----------------------------------------------------------------------------------------------------
    // MARK: Side Effects
    // ----------------------------------------------------------------------------------------------------
    useEffect(() => {
        if (validationErrors.length > 0) {
            setError('identifier', { message: getValidationErrorMessage('identifier', validationErrors) });
            setError('password', { message: getValidationErrorMessage('password', validationErrors) });
            setError('rememberMe', { message: getValidationErrorMessage('remember_me', validationErrors) });
        }
    }, [validationErrors, setError]);

    // ----------------------------------------------------------------------------------------------------
    // MARK: Return
    // ----------------------------------------------------------------------------------------------------
    return {
        isValid,
        isLoading,
        response,
        error,
        validationErrors: errors,
        submit,
        register,
        setValue,
        watch,
    };
}
